import * as http from './http';
import {
  ApiResponse,
  ListColumnSettings,
  MasterData,
  SavedFilter,
  ListEntityType,
  Column,
  Questionaire,
  Showroom,
  Role,
  FilterValueType,
  AppSettings,
  Settings,
  SMSSettings,
  EmailSettings,
  AutolineSettings,
  AzureAdSettings,
  OorjitSettings,
  GeneralSettings,
  ExternalLeadSourceSettings,
  QuotationSettings,
  BookingSettings,
  PreBookingSettings,
  SystemTask,
  ITemplateKeyItem,
  NotificationTemplate,
  ICreateTemplate,
  LeadAssignmentSettings,
  SiblingBrand,
  ListSort,
  PaginatedApiResponse,
  INotificationTemplateItem,
  OneDriveSettingsData,
  LeadActivitySettingsData,
  ShortenUrlSettings,
  PDFSettings,
  ApplicationSettings,
  NotificationSettings,
  SocialMediaLink,
  LeadLostApprovalSettings,
  DiscountSettings,
  AiApiSettings,
  District,
  FollowUpGapTimeSettings,
  FollowupActivitiesOfStage,
  AppNotificationSettings,
  LeadStageField,
  LeadStages,
  Entity,
  WhatsAppSettings,
  InvoiceSettings,
  TradeInMasterData,
  TradeInAccountSettings,
  VehicleReportSettings,
  OCRSettings,
  ActivitySettings,
  UserbackSettings,
  WhatsappLeadAssignmentSettings,
  PackagePriceCalculationSettingsData,
  ServiceAdvisorSettings,
  BodyShopSettings,
  OutlookSettings,
  DeliveryFollowUpGapTimeSettings,
  CompanyAssigneeSettings,
  RequirementSettings,
  CloudFrontSettings,
} from '../models';
import ApiService from './service';
import { IBulkActionUser } from '../models/CrmAgent';
import { stringify } from 'query-string';
import { VisibilityGroup } from '../models/visibilityGroup';
import { Dict } from '../../models';

export class SettingsService extends ApiService {
  public async getMasterData(): Promise<MasterData> {
    const url = `${this.apiDomain}/api/settings/master-data`;
    const response = await http.get<ApiResponse<MasterData>>(url, this.store);
    return response?.data?.data;
  }

  public async getAppSettings(): Promise<AppSettings> {
    const url = `${this.apiDomain}/api/settings/app-settings`;
    const response = await http.get<ApiResponse<AppSettings>>(url, this.store);
    return response?.data?.data;
  }

  public async getSettings(): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings`;
    const response = await http.get<ApiResponse<Settings>>(url, this.store);
    return response?.data?.data;
  }

  public async updateSMSSettings(data: SMSSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/sms`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async updateRequirementSettings(data: RequirementSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/tolerance`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      {tolerance:data.value},
      this.store
    );
    return response?.data?.data;
  }

  public async updateEmailSettings(data: EmailSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/email`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateAutolineSettings(
    data: AutolineSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/autoline`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateAzureAdSettings(data: AzureAdSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/azure-ad`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async updateOutlookSettings(data: OutlookSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/outlook`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateOneDriveSettings(
    data: OneDriveSettingsData
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/oneDrive`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateOorjitSettings(data: OorjitSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/oorjit`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateGeneralSettings(data: GeneralSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/general`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateQuotationSettings(
    data: QuotationSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/quotation`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateSiblingBrandsSettings(data: {
    siblingBrands: SiblingBrand[];
  }): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/sibling-brands`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateLeadAssignmentSettings(
    data: LeadAssignmentSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/lead-assignment`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateWhatsappLeadAssignmentSettings(
    data: WhatsappLeadAssignmentSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/whatsapp-lead-assignment`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateLeadActivitySettings(
    data: LeadActivitySettingsData
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/lead-activity-assignment`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateBookingSettings(data: BookingSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/booking`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updatePreBookingSettings(
    data: PreBookingSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/pre-booking`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateDiscountSettings(
    data: DiscountSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/discount`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateFacebookLeadsApiSettings(
    data: ExternalLeadSourceSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/external/facebook-api`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateTiktokLeadsApiSettings(
    data: ExternalLeadSourceSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/external/tiktok-api`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateLinkedInLeadsApiSettings(
    data: ExternalLeadSourceSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/external/linked-in-api`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async getQuestionaire(
    modelCode?: string,
    variantCode?: string
  ): Promise<Questionaire[]> {
    const url = `${this.apiDomain}/api/questionaire?modelCode=${modelCode}&variantCode=${variantCode}`;

    const response = await http.get<ApiResponse<Questionaire[]>>(
      url,
      this.store
    );

    return response?.data?.data;
  }

  public async getQuestionaireForModel(
    modelCode?: string,
    variantCode?: string
  ): Promise<Questionaire[]> {
    const url = `${this.apiDomain}/api/questionaire/model?modelCode=${modelCode}&variantCode=${variantCode}`;

    const response = await http.get<ApiResponse<Questionaire[]>>(
      url,
      this.store
    );

    return response?.data?.data;
  }

  public async getShowrooms(data?: {
    ecommerceOnly?: boolean;
  }): Promise<Showroom[]> {
    const url = `${this.apiDomain}/api/showroom?${stringify(data || {})}`;
    const response = await http.get<ApiResponse<Showroom[]>>(url, this.store);
    return response?.data?.data;
  }

  public async getCrmAgents(showroomId?: string): Promise<IBulkActionUser[]> {
    let url = `${this.apiDomain}/api/user/crm-agents`;
    if (showroomId != null) {
      url += `?${stringify({
        showroomId,
      })}`;
    }
    const response = await http.get<ApiResponse<IBulkActionUser[]>>(
      url,
      this.store
    );
    return response?.data?.data;
  }

  public async getSalesPersons(showroomId: string): Promise<IBulkActionUser[]> {
    const url = `${this.apiDomain}/api/user/sales-persons?${stringify({
      showroomId,
    })}`;
    const response = await http.get<ApiResponse<IBulkActionUser[]>>(
      url,
      this.store
    );
    return response?.data?.data;
  }

  public async getAssignableSalesPersons(
    selectedLeadIds: string[],
    showroomId: string
  ): Promise<IBulkActionUser[]> {
    const url = `${this.apiDomain}/api/lead/assignable-sales-persons`;
    const response = await http.post<ApiResponse<IBulkActionUser[]>>(
      url,
      { leadIds: selectedLeadIds, showroomId, hideBlocked: true },
      this.store
    );
    return response?.data?.data;
  }

  public async getListColumnSettings(
    type: ListEntityType
  ): Promise<{
    filters: SavedFilter[];
    columnSettings?: ListColumnSettings;
  }> {
    const url = `${this.apiDomain}/api/settings/list-column-settings?type=${type}`;

    const response = await http.get<
      ApiResponse<{
        filters: SavedFilter[];
        columnSettings?: ListColumnSettings;
      }>
    >(url, this.store);
    return response?.data?.data;
  }

  public async saveFilter(
    filters: Dict<FilterValueType>,
    name: string,
    entityType: ListEntityType
  ): Promise<ApiResponse<SavedFilter>> {
    const url = `${this.apiDomain}/api/saved-filter`;

    const resposne = await http.post<ApiResponse<SavedFilter>>(
      url,
      {
        filters: Object.keys(filters).map((key) => {
          return {
            key,
            value: filters[key],
          };
        }),
        name,
        entityType,
      },
      this.store
    );
    return resposne.data;
  }

  public async removeSavedFilter(
    _id: string
  ): Promise<ApiResponse<SavedFilter>> {
    const url = `${this.apiDomain}/api/saved-filter/${_id}`;

    const resposne = await http.remove<ApiResponse<SavedFilter>>(
      url,
      this.store
    );
    return resposne.data;
  }

  public async getRoles(): Promise<Role[]> {
    const url = `${this.apiDomain}/api/role`;

    const response = await http.get<ApiResponse<Role[]>>(url, this.store);

    return response?.data?.data;
  }

  public async getVisibilityGroup(): Promise<VisibilityGroup[]> {
    const url = `${this.apiDomain}/api/visibility-group`;

    const response = await http.get<ApiResponse<VisibilityGroup[]>>(
      url,
      this.store
    );

    return response?.data?.data;
  }

  public async getTemplateKeys(): Promise<ITemplateKeyItem[]> {
    const url = `${this.apiDomain}/api/notification-template/keys`;

    const response = await http.get<ApiResponse<ITemplateKeyItem[]>>(
      url,
      this.store
    );

    return response?.data?.data;
  }

  public async getEventKeys(): Promise<ITemplateKeyItem[]> {
    const url = `${this.apiDomain}/api/notification-template/event/keys`;

    const response = await http.get<ApiResponse<ITemplateKeyItem[]>>(
      url,
      this.store
    );

    return response?.data?.data;
  }

  public async getTemplateContent(key: string): Promise<NotificationTemplate> {
    const url = `${this.apiDomain}/api/notification-template/${key}/all`;

    const response = await http.get<ApiResponse<NotificationTemplate>>(
      url,
      this.store
    );
    return response?.data?.data;
  }

  public async saveListColumnSettings(
    columns: Column[],
    entityType: ListEntityType
  ): Promise<ApiResponse<ListColumnSettings>> {
    const url = `${this.apiDomain}/api/settings/list-column-settings`;

    const resposne = await http.post<ApiResponse<ListColumnSettings>>(
      url,
      {
        columns,
        entityType,
      },
      this.store
    );
    return resposne.data;
  }

  public async getRepeatedSystemTasks(): Promise<SystemTask[]> {
    const url = `${this.apiDomain}/api/system-task`;

    const resposne = await http.get<ApiResponse<SystemTask[]>>(url, this.store);
    return resposne.data?.data;
  }

  public async getNormalSystemTasks(
    data: {
      query?: string;
      page: number;
      search?: string;
      perPage: number;
    } & ListSort
  ): Promise<PaginatedApiResponse<SystemTask>> {
    const url = `${this.apiDomain}/api/tasks/system/normal?${stringify(data)}`;

    const resposne = await http.get<PaginatedApiResponse<SystemTask>>(
      url,
      this.store
    );
    return resposne.data;
  }

  public async scheduleSystemTask(name: string): Promise<SystemTask[]> {
    const url = `${this.apiDomain}/api/system-task/system/schedule`;

    const resposne = await http.post<ApiResponse<SystemTask[]>>(
      url,
      { task: name },
      this.store
    );
    return resposne.data?.data;
  }

  public async updateTemplateContent(
    key: string,
    id: string,
    data: ICreateTemplate
  ): Promise<INotificationTemplateItem> {
    const url = `${this.apiDomain}/api/notification-template/${key}/${id}`;

    const response = await http.put<ApiResponse<INotificationTemplateItem>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async testTemplateContent(data: any): Promise<any> {
    const url = `${this.apiDomain}/api/notification-template/test`;

    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response?.data?.data;
  }
  public async updateShortenUrlSettings(
    data: ShortenUrlSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/shorten-url`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async updatePDFSettings(data: PDFSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/pdf`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateWhatsAppSettings(
    data: WhatsAppSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/whatsapp-api`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateApplicationSettings(
    data: ApplicationSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/application`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateUserbackSettings(
    data: UserbackSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/userback`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateSocialMediaLinks(data: {
    socialMediaLinks: SocialMediaLink[];
  }): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/social-media`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateNotificationSettings(
    data: NotificationSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/notification-settings`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async updateAppNotificationSettings(
    data: AppNotificationSettings[]
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/app-notification-settings`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      { appNotifications: data },
      this.store
    );
    return response?.data?.data;
  }

  public async updateLeadLostApprovalSettings(
    data: LeadLostApprovalSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/lead-lost-approval`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateAiApiSettings(data: AiApiSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/ai-engine-api`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateCloudFrontSettings(
    data: CloudFrontSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/cloudfront`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateOwnerForUnassignedEntities(data: Dict): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/owner-for-unassigned-entity`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async updateOnlineLead(data: Dict): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/online-leads-visibility-groups`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async getDistricts(): Promise<District[]> {
    const url = `${this.apiDomain}/api/district/list`;

    const resposne = await http.get<ApiResponse<District[]>>(url, this.store);
    return resposne.data?.data;
  }

  public async updateFollowUpGapTimeSettings(
    data: FollowUpGapTimeSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/followup-time-difference`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateDeliveryFollowUpGapTimeSettings(
    data: DeliveryFollowUpGapTimeSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/delivery-followup-time-difference`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateFollowupSettings(
    data: FollowupActivitiesOfStage
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/followup-settings`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async getStageDataFields(): Promise<LeadStageField[]> {
    const url = `${this.apiDomain}/api/settings/stage-data-fields`;

    const response = await http.get<ApiResponse<LeadStageField[]>>(
      url,
      this.store
    );

    return response?.data?.data;
  }

  public async updateLeadStageField(
    id: string,
    data: Entity
  ): Promise<LeadStages> {
    const url = `${this.apiDomain}/api/settings/${id}/lead-stage-settings`;
    const response = await http.put<ApiResponse<LeadStages>>(
      url,
      { entity: data },
      this.store
    );
    return response?.data?.data;
  }

  public async updateInvoiceSettings(data: InvoiceSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/invoice`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async getTradeInMasterData(): Promise<TradeInMasterData> {
    const url = `${this.apiDomain}/api/settings/master-data/trade-in`;
    const response = await http.get<ApiResponse<TradeInMasterData>>(
      url,
      this.store
    );
    return response?.data?.data;
  }

  public async updateTradeInAccountSettings(
    data: TradeInAccountSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/trade-in-account`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async updateVehicleReportSettings(
    data: VehicleReportSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/vehicle-report-settings`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async updateOCRSettings(data: OCRSettings): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/ocr-settings`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateActivitySettings(
    data: ActivitySettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/activity`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async UpdatePackagePriceCalculation(
    data: PackagePriceCalculationSettingsData
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/package-price-calculation`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateServiceAdvisorSettings(
    data: ServiceAdvisorSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/service-advisor-settings`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async updateBodyShopSettings(
    data: BodyShopSettings
  ): Promise<Settings> {
    const url = `${this.apiDomain}/api/settings/body-shop-settings`;
    const response = await http.post<ApiResponse<Settings>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }


  public async downloadRequirements(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/requirement/list/download`;
    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Requirement-${new Date()}.xlsx`
    );
  }

  public async updateCompanyAssignment( data: CompanyAssigneeSettings) {
    const url = `${this.apiDomain}/api/settings/company-assignment`;
    const response = await http.post<ApiResponse<Settings>>(
        url,
        data,
        this.store
      );
      return response?.data?.data;
  }
}